import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineSearch } from "react-icons/hi";
import debounce from "lodash/debounce";
import useThemeClass from "utils/hooks/useThemeClass";

// The one search box for every list screen.
//
// It replaces both search boxes the project had:
//   * ChequeGuard's `SearchInput` (controlled, parent debounces via usePagedList)
//   * the per-module `XxxTableSearch.js` (uncontrolled, debounced 500ms, with a
//     magnifier prefix) that existed once per module, 67 copies of it
//
// Both call styles are supported so a module can be migrated without changing
// how its list actually loads:
//
//   controlled   <SearchInput value={queryString} onChange={onSearch} />
//   uncontrolled <SearchInput debounceMs={500} onChange={handleInputChange} />
//
// The focus ring follows the theme colour the user picked instead of the
// hard-coded indigo it used to be.
const SearchInput = forwardRef(
  (
    {
      value,
      onChange,
      placeholder = "Search...",
      className = "w-56",
      debounceMs = 0,
      icon = true,
      disabled = false,
      ...rest
    },
    ref
  ) => {
    const { ringThemeFocus, borderThemeFocus } = useThemeClass();
    const controlled = value !== undefined;
    const [innerValue, setInnerValue] = useState(value ?? "");

    useEffect(() => {
      if (controlled) setInnerValue(value ?? "");
    }, [controlled, value]);

    // Keep the latest handler in a ref so the debounced wrapper is created once
    // and a re-render never drops a pending keystroke.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const emit = useMemo(() => {
      const fire = (val) => onChangeRef.current?.(val);
      return debounceMs > 0 ? debounce(fire, debounceMs) : fire;
    }, [debounceMs]);

    useEffect(() => () => emit.cancel?.(), [emit]);

    const handleChange = (e) => {
      const next = e.target.value;
      setInnerValue(next);
      emit(next);
    };

    const inputClasses = [
      "w-full rounded-md border border-gray-300 py-2 text-sm text-gray-700 placeholder-gray-400",
      "focus:outline-none focus:ring-2",
      ringThemeFocus,
      borderThemeFocus,
      "disabled:cursor-not-allowed disabled:bg-gray-50",
      icon ? "pl-9 pr-3" : "px-3",
    ].join(" ");

    return (
      <div className={`relative ${className}`.trim()}>
        {icon && (
          <HiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        )}
        <input
          ref={ref}
          type="text"
          value={innerValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClasses}
          {...rest}
        />
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
